import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { Loader2, LogOut, Shield, Sparkles } from "lucide-react";

export const Route = createFileRoute("/dashboard")({ component: DashboardPage });

function DashboardPage() {
  const { session, loading, roleLoading, telegramId, isStaff, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!session) { navigate({ to: "/login" }); return; }
    if (!roleLoading && !telegramId) navigate({ to: "/activate" });
  }, [loading, roleLoading, session, telegramId, navigate]);

  if (loading || roleLoading || !session || !telegramId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/60 bg-card/30 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="text-lg font-semibold tracking-tight">
            <span className="text-foreground">Clips</span><span className="text-primary">Flow</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-muted-foreground sm:inline">{session.user.email}</span>
            {isStaff && (
              <Button asChild variant="outline" size="sm">
                <Link to="/admin"><Shield className="mr-2 h-4 w-4" /> Admin</Link>
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => signOut().then(() => navigate({ to: "/login" }))}>
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="rounded-2xl border border-border/60 bg-card/40 p-10 text-center backdrop-blur">
          <div className="mx-auto mb-4 inline-flex rounded-full bg-primary/10 p-3">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Welcome to ClipsFlow</h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Your account is linked. Your clips and stats will appear here.
          </p>
        </div>
      </main>
    </div>
  );
}
